import React, { useState } from 'react'
import { Box, Modal, Typography, IconButton, styled, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Close, PauseCircle, Warning } from '@mui/icons-material'

interface DeactivateAccountModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
}

// ==================== STYLED COMPONENTS ====================
const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  padding: '24px',
})

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '460px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: `
    0 20px 60px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(226, 232, 240, 0.6)
  `,
  overflow: 'hidden',
  outline: 'none',

  [theme.breakpoints.down('sm')]: {
    maxWidth: '92%',
    margin: '0 16px',
  },
}))

const ConfirmButton = styled(LoadingButton)(({ theme }) => ({
  height: '44px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.01em',
  textTransform: 'none',
  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  color: '#ffffff',
  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.25)',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.35)',
  },

  '&:active': {
    transform: 'scale(0.98)',
  },

  '&:disabled': {
    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.4) 0%, rgba(234, 88, 12, 0.4) 100%)',
    color: 'rgba(255, 255, 255, 0.6)',
  },
}))

const CancelButton = styled(LoadingButton)(({ theme }) => ({
  height: '44px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.01em',
  textTransform: 'none',
  backgroundColor: '#ffffff',
  color: '#64748b',
  border: '1.5px solid #e2e8f0',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#cbd5e1',
    color: '#475569',
  },

  '&:active': {
    transform: 'scale(0.98)',
  },
}))

const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState('')
  const [isDeactivating, setIsDeactivating] = useState(false)

  const handleConfirm = async () => {
    if (confirmText.toLowerCase() !== 'deactivate') return

    setIsDeactivating(true)
    try {
      await onConfirm()
      setConfirmText('')
      onClose()
    } finally {
      setIsDeactivating(false)
    }
  }

  const handleClose = () => {
    setConfirmText('')
    onClose()
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ModalOverlay onClick={handleClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              backgroundColor: '#f8fafc',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              zIndex: 10,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: '#fee2e2',
                borderColor: '#fca5a5',
                transform: 'rotate(90deg)',
                '& svg': {
                  color: '#ef4444',
                },
              },
            }}
          >
            <Close sx={{ fontSize: 20, color: '#64748b' }} />
          </IconButton>

          {/* Content */}
          <Box
            sx={{
              padding: { xs: '32px 24px 28px', sm: '36px 32px 32px' },
            }}
          >
            {/* Header with Icon */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PauseCircle sx={{ fontSize: 26, color: '#ffffff' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#0f172a',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                    mb: 0.75,
                  }}
                >
                  Deactivate Account?
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#64748b',
                    lineHeight: 1.5,
                  }}
                >
                  You can reactivate anytime by logging back in
                </Typography>
              </Box>
            </Box>

            {/* Info Box */}
            <Box
              sx={{
                backgroundColor: '#fef3c7',
                borderRadius: '10px',
                padding: '12px 14px',
                mb: 3,
                border: '1px solid #fde68a',
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#78350f',
                  lineHeight: 1.5,
                  mb: 1,
                }}
              >
                What happens when you deactivate:
              </Typography>
              <Box component='ul' sx={{ pl: 2, m: 0, listStyleType: 'disc' }}>
                <Typography
                  component='li'
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#78350f',
                    lineHeight: 1.6,
                    mb: 0.5,
                  }}
                >
                  Profile hidden from others
                </Typography>
                <Typography
                  component='li'
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#78350f',
                    lineHeight: 1.6,
                    mb: 0.5,
                  }}
                >
                  Creations remain private
                </Typography>
                <Typography
                  component='li'
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#78350f',
                    lineHeight: 1.6,
                  }}
                >
                  All data preserved
                </Typography>
              </Box>
            </Box>

            {/* Confirmation Input */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0f172a',
                  mb: 1,
                }}
              >
                Type{' '}
                <Box component='span' sx={{ color: '#f97316', fontWeight: 700 }}>
                  DEACTIVATE
                </Box>{' '}
                to confirm
              </Typography>
              <TextField
                fullWidth
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder='Type DEACTIVATE'
                autoComplete='off'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '44px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 500,
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f97316',
                      borderWidth: '2px',
                    },
                  },
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <CancelButton fullWidth onClick={handleClose} disabled={isDeactivating}>
                Cancel
              </CancelButton>
              <ConfirmButton
                fullWidth
                onClick={handleConfirm}
                loading={isDeactivating}
                disabled={confirmText.toLowerCase() !== 'deactivate' || isDeactivating}
              >
                Deactivate
              </ConfirmButton>
            </Box>
          </Box>
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  )
}

export default DeactivateAccountModal
